import { createFileRoute, isNotFound } from '@tanstack/react-router'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowLeft, faCalendar, faChevronRight, faNewspaper } from '@fortawesome/free-solid-svg-icons'
import { Footer, HeaderMiddle, Navbar, TopBar } from '../components/SiteChrome'
import { NewsCard } from '../components/HomeNewsSection'
import { NewsArticleTracker, NewsListTracker } from '../components/NewsAnalytics'
import { UpstreamUnavailablePage } from '../components/UpstreamUnavailablePage'
import {
  formatPublishedDate,
  getStorefrontConfig,
  placeholderImage,
  toExternalImage,
} from '../lib/dropify'
import type { StorefrontBlogPost, StorefrontConfig } from '../lib/dropify'
import { getDashboardNewsPost, listDashboardNewsArchive } from '../lib/news'
import {
  DEFAULT_OG_IMAGE,
  absoluteUrl,
  blogMetaDescription,
  blogMetaTitle,
  buildBlogPostingSchema,
  buildBreadcrumbSchema,
} from '../lib/seo'
import { storefrontResponseHeaders } from '../lib/storefrontResponse'

const NEWS_HERO_IMAGE = { width: 1400, height: 850, smart: true, format: 'webp', quality: 80 } as const
const NEWS_OG_IMAGE = { width: 1200, height: 630, smart: true, format: 'jpeg', quality: 82 } as const

export const Route = createFileRoute('/berita/$slug')({
  loader: async ({ params }) => {
    const [config, postResult, archive] = await Promise.all([
      getStorefrontConfig().catch(() => null),
      getDashboardNewsPost({ data: params.slug })
        .then((post) => ({ post, unavailable: false }))
        .catch((error) => {
          if (isNotFound(error)) throw error
          return { post: null, unavailable: true }
        }),
      listDashboardNewsArchive({ data: { page: 1, per_page: 4 } }).catch(() => null),
    ])
    const post = postResult.post
    const related = (archive?.items ?? []).filter((item) => !post || item.slug !== post.slug).slice(0, 3)
    return { config, post, related, unavailable: postResult.unavailable }
  },
  headers: ({ loaderData }) => storefrontResponseHeaders(loaderData?.unavailable ?? false),
  head: ({ loaderData, params }) => {
    const post = loaderData?.post
    const path = `/berita/${post?.slug || params.slug}`
    const title = post ? blogMetaTitle(post) : 'Berita dari Dropify'
    const description = post ? blogMetaDescription(post) : 'Detail berita sedang disinkronkan. Lihat arsip berita atau katalog produk grosir Dropify.'
    const image = toExternalImage(post?.featured_image_url, NEWS_OG_IMAGE) || DEFAULT_OG_IMAGE

    return {
      meta: [
        { title },
        { name: 'description', content: description },
        { property: 'og:title', content: title },
        { property: 'og:description', content: description },
        { property: 'og:type', content: 'article' },
        { property: 'og:url', content: absoluteUrl(path) },
        { property: 'og:image', content: image },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: title },
        { name: 'twitter:description', content: description },
        { name: 'twitter:image', content: image },
        ...(!post ? [{ name: 'robots', content: 'noindex, follow' }] : []),
      ],
      links: [{ rel: 'canonical', href: absoluteUrl(path) }],
      scripts: post ? [
        { type: 'application/ld+json', children: JSON.stringify(buildBlogPostingSchema(post, path)) },
        { type: 'application/ld+json', children: JSON.stringify(buildBreadcrumbSchema([
          { name: 'Beranda', path: '/' },
          { name: 'Berita', path: '/berita' },
          { name: post.title, path },
        ])) },
      ] : [],
    }
  },
  component: NewsDetailPage,
})

function NewsDetailPage() {
  const data = Route.useLoaderData()
  const { slug } = Route.useParams()
  if (!data.post) {
    return (
      <UpstreamUnavailablePage
        config={data.config}
        path={`/berita/${slug}`}
        eyebrow="Berita sedang diperbarui"
        title="Detail berita belum dapat dimuat"
        description="Halaman utama, katalog, dan kontak WhatsApp tetap dapat digunakan. Coba muat ulang atau lihat berita lain dari Dropify."
        backHref="/berita"
        backLabel="Kembali ke arsip berita"
      />
    )
  }
  return <LoadedNewsDetailPage config={data.config} post={data.post} related={data.related} />
}

function LoadedNewsDetailPage({ config, post, related }: {
  config: StorefrontConfig | null
  post: StorefrontBlogPost
  related: StorefrontBlogPost[]
}) {
  const publishedDate = formatPublishedDate(post.published_at)

  return (
    <div className="min-h-screen bg-white">
      <TopBar config={config} />
      <HeaderMiddle />
      <Navbar />

      <header className="border-b border-slate-200 bg-slate-50 py-10 md:py-14">
        <div className="mx-auto max-w-4xl px-4">
          <nav className="mb-6 flex items-center gap-2 text-sm text-slate-500">
            <a href="/" className="hover:text-blue-700">Beranda</a>
            <FontAwesomeIcon icon={faChevronRight} className="h-3 w-3" />
            <a href="/berita" className="hover:text-blue-700">Berita</a>
          </nav>
          <div className="mb-5 flex flex-wrap items-center gap-3 text-sm">
            <span className="inline-flex items-center gap-2 rounded-full bg-blue-100 px-3 py-1 font-bold text-blue-700">
              <FontAwesomeIcon icon={faNewspaper} className="h-3.5 w-3.5" /> Berita
            </span>
            {publishedDate && (
              <time dateTime={post.published_at || undefined} className="inline-flex items-center gap-2 text-slate-500">
                <FontAwesomeIcon icon={faCalendar} className="h-3.5 w-3.5" /> {publishedDate}
              </time>
            )}
          </div>
          <h1 className="text-3xl font-black leading-tight tracking-tight text-slate-900 md:text-5xl">{post.title}</h1>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-10 md:py-14">
        <NewsArticleTracker post={post} articleElementId="news-article-content" />
        <img
          src={toExternalImage(post.featured_image_url, NEWS_HERO_IMAGE) || placeholderImage(post.title, 1400, 850)}
          alt={post.featured_image_alt || post.title}
          width="1400"
          height="850"
          className="mb-10 max-h-[680px] w-full rounded-2xl object-cover shadow-sm md:mb-14"
        />

        <article id="news-article-content" className="mx-auto max-w-3xl">
          {post.content ? (
            <div
              className="prose prose-slate max-w-none prose-headings:font-black prose-headings:text-slate-900 prose-headings:scroll-mt-24 prose-a:text-blue-700 prose-img:rounded-xl prose-img:shadow-sm"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />
          ) : (
            <p className="text-slate-600">Isi berita belum tersedia.</p>
          )}
          <a href="/berita" className="mt-12 inline-flex min-h-11 items-center gap-2 rounded-xl border border-slate-300 px-5 py-3 text-sm font-bold text-slate-700 hover:border-blue-300 hover:text-blue-700">
            <FontAwesomeIcon icon={faArrowLeft} className="h-3.5 w-3.5" /> Kembali ke semua berita
          </a>
        </article>

        {related.length > 0 && (
          <section className="mt-16 border-t border-slate-200 pt-12 md:mt-20">
            <NewsListTracker listName="Berita Terkait" posts={related} />
            <h2 className="mb-8 text-2xl font-black tracking-tight text-slate-900">Berita terbaru lainnya</h2>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((item, index) => (
                <NewsCard key={item.id} post={item} listName="Berita Terkait" index={index} />
              ))}
            </div>
          </section>
        )}
      </main>

      <Footer config={config} />
    </div>
  )
}
